import classNames from "classnames";
import React from "react";
import PropTypes from "prop-types";
import styles from "./index.module.css";
import ReportItem from "Reporting/Components/ReportItem";
import { useState, useEffect, useRef } from "react";

/*
 * Table displaying different items in a report with the functionality to sort
 */
export default function ReportTable({ item, index, reports }) {
  const isDiamondData =
    item?.events?.[0]?.diamonds && Array.isArray(item.events[0].diamonds);

  const isIboData = item?.iboData && Array.isArray(item.iboData);

  const PAGE_HEIGHT_PX = 600;
  const iboTableRef = useRef(null);
  const diamondTableRef = useRef(null);
  const ticketTableRef = useRef(null);

  const [iboBreakIndexes, setIboBreakIndexes] = useState([]);
  const [diamondBreakIndexes, setDiamondBreakIndexes] = useState([]);
  const [ticketBreakIndexes, setTicketBreakIndexes] = useState([]);

  const calculateBreaks = (ref, rowCount, rowSelector, setBreaks) => {
    if (!ref.current) {
      console.warn("Ref not available for break calculation");
      return;
    }

    const timeout = setTimeout(() => {
      const allElements = Array.from(
        ref.current.querySelectorAll(`[class*="${rowSelector}"]`)
      );

      const newBreaks = [];

      const headerEl =
        ref.current.querySelector(`.${styles.doubleHeaderWrapper}`) ||
        ref.current.querySelector(`.${styles.header}`);
      const headerHeight = headerEl?.getBoundingClientRect().height || 0;

      let heightSoFar = headerHeight;
      let currentRowIndex = 0;
      let isFirstPage = true;

      allElements.forEach((el, i) => {
        const rect = el.getBoundingClientRect();
        heightSoFar += rect.height;

        if (heightSoFar > PAGE_HEIGHT_PX) {
          newBreaks.push(currentRowIndex);
          heightSoFar = rect.height;
          isFirstPage = false;
        }

        currentRowIndex++;

        if (currentRowIndex === rowCount) {
          newBreaks.push(currentRowIndex);
        }
      });

      setBreaks(newBreaks);
    }, 100);

    return () => clearTimeout(timeout);
  };

  useEffect(() => {
    if (isIboData) {
      return calculateBreaks(
        iboTableRef,
        item.iboData.length,
        styles.pageRowWrapper,
        setIboBreakIndexes
      );
    }
  }, [item.iboData]);

  useEffect(() => {
    if (isDiamondData) {
      const diamondRowCount = item.events.reduce(
        (sum, event) => sum + event.diamonds.length + 1,
        0
      );

      return calculateBreaks(
        diamondTableRef,
        diamondRowCount,
        "pageRowWrapper",
        setDiamondBreakIndexes
      );
    }
  }, [item.events]);

  useEffect(() => {
    if (!isDiamondData && !isIboData) {
      return calculateBreaks(
        ticketTableRef,
        item.events.length + 1,
        "pageRowWrapper",
        setTicketBreakIndexes
      );
    }
  }, [item.events]);

  useEffect(() => {
    const adjustMajorHeaderHeights = () => {
      if (!iboTableRef.current) return;

      const headers = iboTableRef.current.querySelectorAll(
        `.${styles.majorHeader}`
      );
      let maxHeight = 0;

      headers.forEach((el) => {
        el.style.height = "auto";
      });

      headers.forEach((el) => {
        maxHeight = Math.max(maxHeight, el.offsetHeight);
      });

      headers.forEach((el) => {
        el.style.height = `${maxHeight}px`;
      });
    };

    adjustMajorHeaderHeights();
    window.addEventListener("resize", adjustMajorHeaderHeights);

    return () => {
      window.removeEventListener("resize", adjustMajorHeaderHeights);
    };
  }, [iboTableRef]);

  if (isIboData) {
    const IBO_COLUMNS = [
      { key: "ibo", label: "IBO" },
      { key: "name", label: "Name" },
      { key: "pin", label: "Pin" },
    ];

    const columnOrder = [
      "Badges",
      "Other Qualifiers",
      "Breakout Events",
      "Main Event",
    ];

    const orderedHeaders = columnOrder
      .map((section) =>
        item.headers.find((header) => header.header === section)
      )
      .filter(Boolean);

    const headerColumns = orderedHeaders.flatMap((header) =>
      header.columns.map((column) => ({
        section: header.header,
        column,
      }))
    );

    return (
      <div className={index == 1 && styles.test2}>
        <div className={styles.headerInfo}>
          <h2>
            <div className={styles.event}>
              {item.eventName}: {item.eventType}
            </div>
          </h2>
        </div>

        <div className={styles.iboContainer}>
          <div ref={iboTableRef}>
            <div className={styles.doubleHeaderWrapper}>
              {/* IBO Data Major Header and Subheaders */}
              <div className={`${styles.columnGroup} ${styles.iboColumnGroup}`}>
                <div className={styles.majorHeader}>IBO Data</div>
                <div className={styles.subHeaderGroup}>
                  {IBO_COLUMNS.map((col) => (
                    <div
                      key={col.key}
                      className={classNames(
                        styles.subHeader,
                        styles[`col_${col.key}`]
                      )}
                    >
                      {col.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Dynamic Major Headers and Subheaders */}
              {orderedHeaders.map((header) => (
                <div
                  key={header.header}
                  className={`${styles.columnGroup} ${
                    styles[`col_${header.header.replace(/\s+/g, "_")}`]
                  }`}
                >
                  <div className={styles.majorHeader}>{header.header}</div>
                  <div className={styles.subHeaderGroup}>
                    {header.columns.map((col) => (
                      <div
                        key={`${header.header}-${col}`}
                        className={styles.subHeader}
                      >
                        {col}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Data Rows */}
            {item.iboData.map((ibo, index) => (
              <React.Fragment key={ibo.IBO}>
                {iboBreakIndexes.includes(index) && (
                  <div className={styles.pageBreak} />
                )}
                <div className={styles.pageRowWrapper}>
                  <ReportItem
                    item={ibo}
                    isOdd={index % 2 !== 0}
                    ticketMeta={headerColumns}
                    isIbo
                    orderedHeaders={orderedHeaders}
                    iboColumns={IBO_COLUMNS}
                  />
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        {index !== reports.length - 1 && (
          <div className={styles.reportDivider} />
        )}

        {iboBreakIndexes.includes(item.iboData.length) &&
          index !== reports.length - 1 && <div className={styles.pageBreak} />}
      </div>
    );
  }

  if (isDiamondData) {
    const ticketMeta = Array.from(
      new Set(
        item.events.flatMap((event) =>
          event.diamonds.flatMap((diamond) =>
            diamond.tickets.map((ticket) => ticket.ticket)
          )
        )
      )
    ).map((ticketName, index) => ({
      ticketName,
      order: index,
    }));

    return (
      <div className={index === 0 && styles.test2}>
        <div className={styles.headerInfo}>
          <h2>
            <span className={styles.summary}>
              Ticket Purchases {item.eventOwner && "- "}
            </span>
            <span className={styles.event}>
              {item.eventOwner}: {item.eventType}
            </span>
            <span className={styles.date}> - {item.year}</span>
          </h2>
        </div>

        <div className={styles.iboContainer}>
          <div ref={diamondTableRef}>
            {item.events.map((event, eventIndex) => {
              const ticketTotalsMap = {};

              event.diamonds.forEach((diamond) => {
                diamond.tickets.forEach((ticket) => {
                  if (!ticketTotalsMap[ticket.ticket]) {
                    ticketTotalsMap[ticket.ticket] = {
                      ticket: ticket.ticket,
                      count: 0,
                      isAttending: !!ticket.isAttending,
                    };
                  }
                  ticketTotalsMap[ticket.ticket].count += ticket.count || 0;
                  ticketTotalsMap[ticket.ticket].isAttending =
                    ticketTotalsMap[ticket.ticket].isAttending ||
                    !!ticket.isAttending;
                });
              });

              const totalItem = {
                diamond: "Total",
                tickets: Object.values(ticketTotalsMap),
              };

              const eventTicketNames = new Set();
              event.diamonds.forEach((diamond) => {
                diamond.tickets.forEach((ticket) => {
                  if (ticket.count > 0) {
                    eventTicketNames.add(ticket.ticket);
                  }
                });
              });

              const filteredTicketMeta = ticketMeta.filter((ticket) =>
                eventTicketNames.has(ticket.ticketName)
              );

              return (
                <div key={eventIndex} className={styles.eventSection}>
                  <div className={styles.eventHeader}>
                    {event.eventName}{" "}
                    {event.eventDate && ` - ${event.eventDate}`}
                  </div>

                  <div className={styles.container}>
                    <div className={styles.header}>
                      <div className={styles.diamond}>Diamond</div>
                      {filteredTicketMeta.map((ticket) => (
                        <div key={ticket.ticketName} className={styles.extra}>
                          {ticket.ticketName}
                        </div>
                      ))}
                      <div className={styles.extra}>Total</div>
                    </div>

                    <div className={styles.pageRowWrapper}>
                      <ReportItem
                        item={totalItem}
                        isOdd={event.diamonds.length % 2 === 1}
                        type="total"
                        ticketMeta={filteredTicketMeta}
                        isDiamond={true}
                      />
                    </div>

                    {event.diamonds.map((diamond, diamondIndex) => {
                      const globalIndex =
                        item.events
                          .slice(0, eventIndex)
                          .reduce((sum, e) => sum + e.diamonds.length + 1, 0) +
                        diamondIndex;

                      return (
                        <React.Fragment key={diamond.diamond}>
                          {diamondBreakIndexes.includes(globalIndex) && (
                            <div className={styles.pageBreak} />
                          )}
                          <div className={styles.pageRowWrapper}>
                            <ReportItem
                              item={diamond}
                              isOdd={diamondIndex % 2 === 1}
                              ticketMeta={filteredTicketMeta}
                              isDiamond
                            />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Divider after entire report unless it's the last one */}
        {index !== reports.length - 1 && (
          <div className={styles.reportDivider} />
        )}

        {diamondBreakIndexes.includes(
          item.events.reduce((sum, e) => sum + e.diamonds.length + 1, 0)
        ) &&
          index !== reports.length - 1 && <div className={styles.pageBreak} />}
      </div>
    );
  }

  // === Default: Ticket-Based Display ===

  const ticketMeta = Array.from(
    new Set(
      item.events.flatMap((event) =>
        event.tickets.map((ticket) => ticket.ticket)
      )
    )
  ).map((ticketName, index) => ({
    ticketName,
    order: index,
  }));

  const ticketTotalsMap = {};

  item.events.forEach((event) => {
    event.tickets.forEach((ticket) => {
      if (!ticketTotalsMap[ticket.ticket]) {
        ticketTotalsMap[ticket.ticket] = {
          ticket: ticket.ticket,
          count: 0,
          virtualCount: 0,
          isAttending: !!ticket.isAttending,
        };
      }

      ticketTotalsMap[ticket.ticket].count += ticket.count || 0;
      ticketTotalsMap[ticket.ticket].isAttending =
        ticketTotalsMap[ticket.ticket].isAttending || !!ticket.isAttending;
      if (ticket.virtualCount !== undefined) {
        ticketTotalsMap[ticket.ticket].virtualCount += ticket.virtualCount;
      }
    });
  });

  const totalItem = {
    eventName: `Total`,
    eventDate: "",
    tickets: Object.values(ticketTotalsMap),
  };

  return (
    <div className={index === 1 && styles.test2}>
      {index !== 0 && <div className={styles.pageBreak} />}
      <div className={styles.headerInfo}>
        <h2>
          <span className={styles.summary}>
            Ticket Purchases {item.eventOwner && "- "}
          </span>
          <span className={styles.event}>
            {item.eventOwner}: {item.eventType}
          </span>
          <span className={styles.date}> - {item.year}</span>
        </h2>
      </div>

      <div className={styles.iboContainer}>
        <div className={styles.container} ref={ticketTableRef}>
          <div className={styles.header}>
            <div className={styles.diamond}>Event</div>
            {ticketMeta.map((ticket) => (
              <div key={ticket.ticketName} className={styles.extra}>
                {ticket.ticketName}
              </div>
            ))}
            <div className={styles.extra}>Total Tickets</div>
          </div>

          {item.events.length > 1 && (
            <React.Fragment key="total">
              {ticketBreakIndexes.includes(item.events.length) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItem
                  item={totalItem}
                  isOdd={item.events.length % 2 !== 0}
                  type="total"
                  ticketMeta={ticketMeta}
                />
              </div>
            </React.Fragment>
          )}

          {item.events.map((event, i) => (
            <React.Fragment key={i}>
              {ticketBreakIndexes.includes(i) && (
                <div className={styles.pageBreak} />
              )}
              <div className={styles.pageRowWrapper}>
                <ReportItem
                  item={event}
                  isOdd={i % 2 !== 0}
                  type="event"
                  ticketMeta={ticketMeta}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Optional: Divider after report, unless last one */}
      {index !== reports.length - 1 && <div className={styles.reportDivider} />}
    </div>
  );
}

ReportTable.propTypes = {
  item: PropTypes.shape({
    eventOwner: PropTypes.string,
    eventType: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    events: PropTypes.arrayOf(PropTypes.object),
  }),
};
